import React, { useState, useEffect, useRef } from "react";
import { Contact, getAvatarColor } from "./types";
import { resizeImageToBase64, isValidImageFile } from "../utils/imageResize";

interface EditContactModalProps {
  contact: Contact;
  onSave: (updates: Partial<Contact>) => void;
  onCancel: () => void;
  isSaving: boolean;
  allSources: string[];
}

const EditContactModal: React.FC<EditContactModalProps> = ({
  contact,
  onSave,
  onCancel,
  isSaving,
  allSources,
}) => {
  const [formData, setFormData] = useState({
    title: contact.title || "",
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    email: contact.email || "",
    mobile_phone: contact.mobile_phone || "",
    home_phone: contact.home_phone || "",
    work_phone: contact.work_phone || "",
    street_address: contact.street_address || "",
    suburb: contact.suburb || "",
    postcode: contact.postcode || "",
    state: contact.state || "",
    notes: contact.notes || "",
    is_unsubscribed: contact.is_unsubscribed || false,
    is_bounced: contact.is_bounced || false,
  });
  const [selectedSources, setSelectedSources] = useState<Set<string>>(
    new Set(contact.source_boards)
  );
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const sourcesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile photo state
  const [profilePhoto, setProfilePhoto] = useState<string | null>(contact.profile_photo || null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Initial values for comparison (computed once per contact)
  const initialFormData = {
    title: contact.title || "",
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    email: contact.email || "",
    mobile_phone: contact.mobile_phone || "",
    home_phone: contact.home_phone || "",
    work_phone: contact.work_phone || "",
    street_address: contact.street_address || "",
    suburb: contact.suburb || "",
    postcode: contact.postcode || "",
    state: contact.state || "",
    notes: contact.notes || "",
    is_unsubscribed: contact.is_unsubscribed || false,
    is_bounced: contact.is_bounced || false,
  };
  const initialSources = [...contact.source_boards].sort().join(",");
  const initialPhoto = contact.profile_photo || null;

  // Track if form has changes (generic comparison)
  const hasChanges =
    JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
    [...selectedSources].sort().join(",") !== initialSources ||
    profilePhoto !== initialPhoto;

  // Close sources dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sourcesRef.current && !sourcesRef.current.contains(e.target as Node)) {
        setIsSourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle Escape key to close modal or dropdown
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving) {
        if (isSourcesOpen) {
          setIsSourcesOpen(false);
        } else {
          onCancel();
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onCancel, isSaving, isSourcesOpen]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "email"
        ? value !== (contact.email || "")
          ? { is_bounced: false }
          : { is_bounced: contact.is_bounced || false }
        : {}),
    }));
  };

  const toggleSource = (source: string) => {
    setSelectedSources((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(source)) {
        newSet.delete(source);
      } else {
        newSet.add(source);
      }
      return newSet;
    });
  };

  // Handle photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoError(null);

    if (!isValidImageFile(file)) {
      setPhotoError("Please upload a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setPhotoError("Image must be smaller than 10MB");
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const base64 = await resizeImageToBase64(file, 100, 0.9);
      setProfilePhoto(base64);
    } catch {
      setPhotoError("Failed to process image. Please try another file.");
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Remove photo
  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setPhotoError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updates: Partial<Contact> = {
      title: formData.title || null,
      first_name: formData.first_name || null,
      last_name: formData.last_name || null,
      full_name: [formData.first_name, formData.last_name].filter(Boolean).join(" ") || null,
      email: formData.email || null,
      mobile_phone: formData.mobile_phone || null,
      home_phone: formData.home_phone || null,
      work_phone: formData.work_phone || null,
      street_address: formData.street_address || null,
      source_boards: Array.from(selectedSources),
      suburb: formData.suburb || null,
      postcode: formData.postcode || null,
      state: formData.state || null,
      notes: formData.notes || null,
      is_unsubscribed: formData.is_unsubscribed,
      is_bounced: formData.is_bounced,
      profile_photo: profilePhoto,
    };
    onSave(updates);
  };

  const inputClass =
    "w-full pl-10 pr-3 py-2.5 rounded-sm bg-navy-mid/50 border border-border-strong text-white text-sm placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all";
  const inputClassNoIcon =
    "w-full px-3 py-2.5 rounded-sm bg-navy-mid/50 border border-border-strong text-white text-sm placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all";
  const labelClass = "block text-xs font-medium text-text-secondary mb-1.5";

  // Get preview initials and color
  const previewName =
    [formData.first_name, formData.last_name].filter(Boolean).join(" ") || contact.full_name;
  const previewInitials =
    ((formData.first_name?.[0] || "") + (formData.last_name?.[0] || "")).toUpperCase() || "?";
  const avatarColor = getAvatarColor(previewName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-navy rounded border border-border-strong max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with contact preview and photo upload */}
        <div className="px-6 py-5 border-b border-border-strong bg-gradient-to-r from-navy to-navy/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Photo preview with upload */}
              <div className="relative group">
                {profilePhoto ? (
                  <>
                    <img
                      src={profilePhoto}
                      alt={previewName || "Contact"}
                      className="w-14 h-14 rounded object-cover shadow-lg"
                    />
                    {/* Edit photo button - only shown when photo exists */}
                    {!isUploadingPhoto ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-rose-accent hover:bg-rose-bright text-white flex items-center justify-center shadow-lg transition-colors border-2 border-navy"
                        title="Change photo"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </button>
                    ) : (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-rose-accent text-white flex items-center justify-center shadow-lg border-2 border-navy">
                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      </div>
                    )}
                  </>
                ) : (
                  /* When no photo, entire avatar is clickable */
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingPhoto}
                    className={`flex-shrink-0 w-14 h-14 rounded bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white text-lg font-semibold shadow-lg cursor-pointer hover:opacity-90 transition-opacity relative`}
                    title="Add photo"
                  >
                    {isUploadingPhoto ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        {previewInitials}
                        {/* Small camera indicator */}
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-rose-accent text-white flex items-center justify-center shadow-lg border-2 border-navy">
                          <svg
                            className="w-2.5 h-2.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </div>
                      </>
                    )}
                  </button>
                )}
                {/* Remove button (only shown when photo exists) */}
                {profilePhoto && !isUploadingPhoto && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors border-2 border-navy"
                    title="Remove photo"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{previewName || "New Contact"}</h3>
                <p className="text-sm text-text-secondary">{formData.email || "No email"}</p>
                {photoError && <p className="text-xs text-red-400 mt-0.5">{photoError}</p>}
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-sm text-text-secondary hover:text-white hover:bg-navy-mid transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* Personal Information Section */}
          <div className="p-6 border-b border-border-subtle">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-4 h-4 text-rose-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider">
                Personal Information
              </h4>
            </div>
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-6 sm:col-span-1">
                <label className={labelClass}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="Dr."
                  className={inputClassNoIcon}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <label className={labelClass}>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="First name"
                  className={inputClassNoIcon}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className={labelClass}>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Last name"
                  className={inputClassNoIcon}
                />
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="p-6 border-b border-border-subtle">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-4 h-4 text-emerald-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider">
                Contact Information
              </h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Email Address</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="email@example.com"
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Mobile Phone</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    value={formData.mobile_phone}
                    onChange={(e) => handleChange("mobile_phone", e.target.value)}
                    placeholder="0400 000 000"
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Home Phone</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    value={formData.home_phone}
                    onChange={(e) => handleChange("home_phone", e.target.value)}
                    placeholder="(03) 0000 0000"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Work Phone</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    value={formData.work_phone}
                    onChange={(e) => handleChange("work_phone", e.target.value)}
                    placeholder="(03) 0000 0000"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Email Preferences Section */}
          <div className="p-6 border-b border-border-subtle">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-4 h-4 text-rose-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
              <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider">
                Email Preferences
              </h4>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.is_unsubscribed}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_unsubscribed: e.target.checked }))
                    }
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 rounded border border-border-strong bg-navy-mid/50 peer-checked:bg-rose-500 peer-checked:border-rose-500 flex items-center justify-center transition-colors group-hover:border-text-secondary">
                    <svg
                      className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-text-primary">Unsubscribed from emails</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    This contact will not be selectable for bulk emails.
                  </p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.is_bounced}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_bounced: e.target.checked }))
                    }
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 rounded border border-border-strong bg-navy-mid/50 peer-checked:bg-rose-500 peer-checked:border-rose-500 flex items-center justify-center transition-colors group-hover:border-text-secondary">
                    <svg
                      className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-text-primary">Email bounced</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    This contact will not be selectable for bulk emails. Automatically cleared if the email address is changed.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Address Section */}
          <div className="p-6 border-b border-border-subtle">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-4 h-4 text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider">
                Address
              </h4>
            </div>
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-6">
                <label className={labelClass}>Street Address</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={formData.street_address}
                    onChange={(e) => handleChange("street_address", e.target.value)}
                    placeholder="123 Main Street"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className={labelClass}>Suburb</label>
                <input
                  type="text"
                  value={formData.suburb}
                  onChange={(e) => handleChange("suburb", e.target.value)}
                  placeholder="Suburb"
                  className={inputClassNoIcon}
                />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <label className={labelClass}>State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  placeholder="VIC"
                  className={inputClassNoIcon}
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <label className={labelClass}>Postcode</label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => handleChange("postcode", e.target.value)}
                  placeholder="3000"
                  className={inputClassNoIcon}
                />
              </div>
            </div>
          </div>

          {/* Source Section */}
          <div className="p-6 border-b border-border-subtle">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-4 h-4 text-cyan-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider">
                Source
              </h4>
            </div>
            <div className="relative" ref={sourcesRef}>
              <button
                type="button"
                onClick={() => setIsSourcesOpen(!isSourcesOpen)}
                className={`${inputClassNoIcon} text-left flex items-center justify-between`}
              >
                <span className={selectedSources.size === 0 ? "text-text-muted" : "text-white"}>
                  {selectedSources.size === 0
                    ? "Select sources..."
                    : selectedSources.size === 1
                      ? Array.from(selectedSources)[0].split("/").pop()
                      : `${selectedSources.size} sources selected`}
                </span>
                <svg
                  className={`w-4 h-4 text-text-muted transition-transform ${isSourcesOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isSourcesOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 max-h-48 overflow-y-auto rounded-sm bg-navy-mid border border-border-strong shadow-xl z-10">
                  {allSources.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-text-secondary">No sources available</div>
                  ) : (
                    allSources.map((source) => {
                      const isSelected = selectedSources.has(source);
                      const shortName = source.split("/").pop() || source;
                      const prefix = source.includes("/") ? source.split("/")[0] : null;

                      return (
                        <button
                          key={source}
                          type="button"
                          onClick={() => toggleSource(source)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-navy-light/50 transition-colors ${
                            isSelected ? "bg-rose-glow" : ""
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                              isSelected
                                ? "bg-rose-accent border-rose-accent"
                                : "border-text-muted hover:border-text-secondary"
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span
                              className={`text-sm ${isSelected ? "text-white" : "text-text-primary"}`}
                            >
                              {shortName}
                            </span>
                            {prefix && (
                              <span className="text-xs text-text-muted ml-2">({prefix})</span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {/* Selected sources tags */}
              {selectedSources.size > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {Array.from(selectedSources).map((source) => (
                    <span
                      key={source}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-navy-mid text-xs text-text-primary"
                    >
                      {source.split("/").pop()}
                      <button
                        type="button"
                        onClick={() => toggleSource(source)}
                        className="text-text-muted hover:text-text-primary transition-colors"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-4 h-4 text-rose-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider">Notes</h4>
            </div>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Additional notes about this contact..."
              rows={4}
              className={`${inputClassNoIcon} resize-none`}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-strong bg-navy/80 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-sm bg-navy-mid text-text-primary hover:bg-navy-light transition-colors disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges}
            className={`px-5 py-2.5 rounded-sm transition-colors flex items-center gap-2 font-medium ${
              hasChanges
                ? "bg-rose-accent text-navy-darkest hover:bg-rose-bright shadow-lg shadow-rose-accent/15"
                : "bg-navy-mid text-text-muted cursor-not-allowed"
            } disabled:opacity-50`}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContactModal;
